import { useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { useMutation } from '@apollo/client/react'

import ActionContainer from 'js/components/ActionContainer/ActionContainer'
import Advanced from 'js/components/EntityActions/Advanced/Advanced'
import DeleteModal from 'js/components/EntityActions/DeleteModal'
import General from 'js/components/EntityActions/General/General'
import FormItem from 'js/components/Form/FormItem'
import { Container } from 'js/components/Grid'
import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import Sticky from 'js/components/Sticky/Sticky'
import { MAX_LENGTH } from 'js/lib/constants'
import {
    ADD_MAGAZINE_ARTICLE,
    EDIT_MAGAZINE_ARTICLE,
} from 'js/magazine-issue/AddEditArticle/AddEditForm/AddEditForm.queries'

import styles from '../../AddEdit/AddEditForm/AddEditForm.module.css'

type AddEditFormProps = {
    site: { guid: string; language?: string }
    viewer: { guid: string; canInsertMedia?: boolean }
    container: { guid: string; url: string }
    entity?: {
        guid: string
        title?: string | null
        abstract?: string | null
        richDescription?: string | null
        backgroundColor?: string
        inputLanguage?: string
        isTranslationEnabled?: boolean
        statusPublished?: string
        canEditAdvanced?: boolean
        owner?: {
            guid: string
            name: string
        }
    } | null
    title: string
    subtype?: string
}

const AddEditForm = ({
    site,
    viewer,
    container,
    entity,
    title,
    subtype = 'blog',
}: AddEditFormProps) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const refFeatured = useRef<{ getValue: () => unknown } | null>(null)

    const [tab, setTab] = useState('general')

    const isPublished = entity?.statusPublished === 'published'

    const [ownerGuid, setOwnerGuid] = useState(entity?.owner?.guid || '')

    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const toggleDeleteModal = () => setShowDeleteModal(!showDeleteModal)

    const afterDelete = () => {
        navigate(container.url, {
            replace: true,
        })
    }

    const handleClose = () => {
        navigate(
            (location?.state as { prevPathname?: string })?.prevPathname ||
                container.url,
        )
    }

    const [addArticle] = useMutation<any>(ADD_MAGAZINE_ARTICLE)
    const [editArticle] = useMutation<any>(EDIT_MAGAZINE_ARTICLE)

    const submit = async ({
        title,
        abstract,
        richDescription,
        inputLanguage,
        isTranslationEnabled,
        backgroundColor,
    }: {
        title: string
        abstract: string | null
        richDescription: string | null
        inputLanguage: string
        isTranslationEnabled: boolean
        backgroundColor: string
    }) => {
        const mutate = entity ? editArticle : addArticle

        await mutate({
            variables: {
                input: {
                    ...(entity
                        ? { guid: entity.guid }
                        : {
                              containerGuid: container.guid,
                              subtype,
                          }),
                    title,
                    abstract,
                    featured:
                        refFeatured.current && refFeatured.current.getValue(),
                    richDescription,
                    backgroundColor,
                    inputLanguage,
                    isTranslationEnabled,
                    ...(settingOptions.advanced && ownerGuid
                        ? { ownerGuid }
                        : {}),
                },
            },
        })
            .then(({ data }) => {
                if (entity) {
                    navigate(data.editEntity.entity.url, {
                        replace: true,
                        state: {
                            prevPathname: location.state?.prevPathname,
                        },
                    })
                } else {
                    navigate(data.addEntity.entity.url, {
                        replace: true,
                        state: {
                            prevPathname: location.state?.prevPathname,
                        },
                    })
                }
            })
            .catch((errors) => {
                console.error(errors)
            })
    }

    const defaultValues = {
        title: entity?.title || null,
        abstract: entity?.abstract || null,
        richDescription: entity?.richDescription || null,
        backgroundColor: entity?.backgroundColor || '',
        inputLanguage: entity?.inputLanguage || site?.language,
        isTranslationEnabled: entity ? entity.isTranslationEnabled : true, // Default to true
    }

    const formMethods = useForm({
        mode: 'onChange',
        defaultValues,
    })

    const {
        control,
        handleSubmit,
        formState: { errors, isValid, isSubmitting },
    } = formMethods

    const submitForm = handleSubmit(submit)

    const { canInsertMedia } = viewer

    const settingOptions = {
        abstract: true,
        featured: canInsertMedia,
        backgroundColor: true,
        advanced: entity?.canEditAdvanced,
    }

    const showTabs = settingOptions.advanced

    return (
        <ActionContainer
            title={title}
            publish={submitForm}
            isValid={isValid}
            isPublishing={isSubmitting}
            isPublished={isPublished}
            onDelete={entity ? toggleDeleteModal : null}
            onClose={handleClose}
        >
            <FormProvider {...formMethods}>
                <Container
                    size="small"
                    style={{ marginBottom: showTabs ? 8 : 24 }}
                >
                    <FormItem
                        control={control}
                        type="text"
                        name="title"
                        label={t('form.title')}
                        size="large"
                        errors={errors}
                        required
                        maxLength={MAX_LENGTH.title}
                    />
                </Container>

                {showTabs && (
                    <Sticky className={styles.tabMenuWrapper}>
                        <Container size="small">
                            <TabMenu
                                label={t('global.settings')}
                                options={[
                                    {
                                        onClick: () => setTab('general'),
                                        label: t('global.general'),
                                        isActive: tab === 'general',
                                        id: 'tab-general',
                                        ariaControls: 'panel-general',
                                    },
                                    ...(settingOptions.advanced
                                        ? [
                                              {
                                                  onClick: () =>
                                                      setTab('advanced'),
                                                  label: t('global.advanced'),
                                                  isActive: tab === 'advanced',
                                                  id: 'tab-advanced',
                                                  ariaControls:
                                                      'panel-advanced',
                                              },
                                          ]
                                        : []),
                                ]}
                                edgePadding
                                edgeMargin
                                showBorder
                                style={{ position: 'static' }}
                            />
                        </Container>
                    </Sticky>
                )}

                <TabPanel
                    id="panel-general"
                    visible={tab === 'general'}
                    aria-labelledby="tab-general"
                >
                    <General
                        {...({
                            ref: refFeatured,
                            settingOptions,
                            entity,
                            site,
                            viewer,
                            subtype,
                            featuredFullHeight: true,
                        } as any)}
                    />
                </TabPanel>

                {entity?.canEditAdvanced && (
                    <TabPanel
                        id="panel-advanced"
                        visible={tab === 'advanced'}
                        aria-labelledby="tab-advanced"
                    >
                        <Advanced
                            {...({
                                settingOptions,
                                entity,
                                ownerGuid,
                                setOwnerGuid,
                            } as any)}
                        />
                    </TabPanel>
                )}
            </FormProvider>

            <DeleteModal
                isVisible={showDeleteModal}
                onClose={toggleDeleteModal}
                title={t('entity-magazine.delete-article')}
                entity={entity}
                afterDelete={afterDelete}
            />
        </ActionContainer>
    )
}

export default AddEditForm
